import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@dreamtree-org/twreact-ui';
import HeroSection from '@/components/home/HeroSection';
import StatsBand from '@/components/sections/StatsBand';
import ClientsStrip from '@/components/sections/ClientsStrip';
import Testimonials from '@/components/sections/Testimonials';
import GoogleReviews from '@/components/sections/GoogleReviews';
import WhyChooseUs from '@/components/sections/WhyChooseUs';
import SectionHeading from '@/components/site/SectionHeading';
import CtaBand from '@/components/site/CtaBand';
import Icon from '@/components/site/Icon';
import SpotlightCards from '@/components/ui/SpotlightCards';
import Reveal from '@/components/ui/Reveal';
import { usePage } from '@site/content';
import { useServices } from '@api/public';
import useSeo from '@/lib/useSeo';

export default function Home() {
  const page = usePage('home');
  const fire = useServices('fire');
  const security = useServices('security');
  useSeo({ description: page.hero.text, image: page.hero.image });

  const preview = [...fire.slice(0, 4), ...security.slice(0, 2)].map((s) => ({
    icon: s.icon,
    title: s.title,
    text: s.summary,
    image: s.image,
    to: '/services',
  }));

  return (
    <>
      <HeroSection hero={page.hero} />
      <StatsBand stats={page.stats} />

      {/* What we are + turnkey steps */}
      <section className="py-16 sm:py-24">
        <div className="site-container grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal className="relative">
            <img src={page.intro.image} alt={page.intro.imageAlt} loading="lazy" className="aspect-[4/5] w-full rounded-[2rem] object-cover sm:aspect-[5/4] lg:aspect-[4/5]" />
            <div className="absolute -bottom-6 -right-2 hidden rounded-2xl bg-ink-950 p-5 text-white shadow-xl sm:block lg:-right-8">
              <p className="font-display text-4xl font-extrabold uppercase text-accent-400">Turnkey</p>
              <p className="text-sm text-ink-300">From site visit to AMC</p>
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow={page.intro.eyebrow} title={page.intro.title} />
            <p className="mt-5 text-pretty leading-relaxed text-ink-600">{page.intro.paragraph1}</p>
            <p className="mt-4 text-pretty leading-relaxed text-ink-600">{page.intro.paragraph2}</p>
            <ol className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {page.intro.steps.map((step, i) => (
                <li key={step} className="rounded-2xl border border-[var(--app-border)] bg-white p-4">
                  <span className="font-display text-2xl font-extrabold text-primary-600">{String(i + 1).padStart(2, '0')}</span>
                  <span className="mt-1 block text-sm font-medium leading-tight text-ink-800">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="bg-ink-50 py-16 sm:py-24">
        <div className="site-container">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionHeading eyebrow={page.services.eyebrow} title={page.services.title} text={page.services.text} />
            <Button as={Link} to="/services" variant="outline" rightIcon={<ArrowRight className="h-4 w-4" />} className="shrink-0">
              All services
            </Button>
          </div>
          <SpotlightCards className="mt-14" items={preview} />
        </div>
      </section>

      {/* Benefits */}
      <section className="relative isolate overflow-hidden bg-ink-950 py-16 text-white sm:py-24">
        <img src="/images/hero/fire-crew.webp" alt="" loading="lazy" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-25" />
        <div className="site-container">
          <SectionHeading eyebrow={page.benefits.eyebrow} title={page.benefits.title} tone="dark" />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {page.benefits.items.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-flame-x">
                    <Icon name={b.icon} className="h-6 w-6 text-white" />
                  </span>
                  <h3 className="mt-5 text-2xl">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-300">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <WhyChooseUs eyebrow={page.why.eyebrow} title={page.why.title} items={page.why.items} />

      {/* Founder */}
      <section className="pb-16 sm:pb-24">
        <div className="site-container">
          <div className="grid items-center gap-10 overflow-hidden rounded-[2rem] bg-gradient-to-br from-primary-700 to-primary-900 text-white lg:grid-cols-[1fr_1.3fr]">
            <img src={page.founder.image} alt={page.founder.imageAlt} loading="lazy" className="h-full max-h-[32rem] w-full object-cover object-top" />
            <div className="p-8 sm:p-12 lg:pl-0">
              <p className="eyebrow !text-accent-300">{page.founder.eyebrow}</p>
              <blockquote className="mt-4 font-display text-3xl font-bold uppercase leading-tight sm:text-4xl">“{page.founder.quote}”</blockquote>
              <p className="mt-5 text-pretty leading-relaxed text-primary-100">{page.founder.text}</p>
              <p className="mt-6 font-semibold">{page.founder.name}</p>
              <p className="text-sm text-primary-200">{page.founder.role}</p>
              <Button as={Link} to="/about" variant="outline" className="mt-6 !border-white/40 !text-white hover:!bg-white/10" rightIcon={<ArrowRight className="h-4 w-4" />}>
                Read more
              </Button>
            </div>
          </div>
        </div>
      </section>

      <ClientsStrip eyebrow={page.clients.eyebrow} title={page.clients.title} />
      <GoogleReviews />
      <Testimonials eyebrow={page.testimonials.eyebrow} title={page.testimonials.title} />
      <CtaBand />
    </>
  );
}
